import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Wrench, MapPin, Phone, Navigation, ChevronDown, Headphones, ExternalLink, Gamepad2, Smartphone } from "lucide-react";

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
import villatechLogo from "@/assets/villatech-logo.png";
import villatechWingIcon from "@/assets/villatech-wing-icon.png";
import remaxWatchImg from "@/assets/products/remax-watch.png";
import xoAirpodsImg from "@/assets/products/xo-airpods.png";

const categories = [
  { icon: Gamepad2, label: "Gaming", color: "text-purple-400" },
  { icon: Headphones, label: "Audio", color: "text-blue-400" },
  { icon: Smartphone, label: "Celular", color: "text-green-400" },
];

const stores = [
  {
    name: "Villatech Metrópoli",
    location: "Centro Comercial Metrópoli, Cartago",
    phone: "+506 2572 0131",
    waze: "https://www.waze.com/live-map/directions?to=ll.9.867203%2C-83.942049"
  },
  {
    name: "Villatech Mall San Pedro",
    location: "Mall San Pedro, 2º piso, frente a UsaFlex, San José",
    phone: "+506 2524 2946",
    waze: "https://www.waze.com/live-map/directions?to=ll.9.934237%2C-84.056332"
  },
  {
    name: "Villatech Paseo de las Flores",
    location: "Mall Paseo de las Flores, frente al BCR, Heredia",
    phone: "+506 2262 3807",
    waze: "https://www.waze.com/live-map/directions?to=ll.9.984663%2C-84.110792"
  },
  {
    name: "Villatech Santa Ana",
    location: "Santa Ana Town Center, primer piso, frente a Subway",
    phone: "+506 4082 6297",
    waze: "https://www.waze.com/live-map/directions?to=ll.9.934323%2C-84.186924"
  },
  {
    name: "Villatech Heredia Centro",
    location: "Mega Super Heredia Centro, Avenida 4, Calle 1",
    phone: "+506 4081 3446",
    waze: "https://www.waze.com/live-map/directions?to=ll.9.997323%2C-84.116156"
  }
];

const offerProducts = [
  {
    name: "REMAX Watch 17",
    description: "Monitoreo de salud, llamadas y pantalla táctil",
    url: "https://villatech-rbi-ai-relojes.lovable.app/",
    image: remaxWatchImg
  },
  {
    name: "Audífonos XO-Q5Pods",
    description: "Sonido envolvente, carga rápida y compatibilidad universal",
    url: "https://villatech-rbi-ai-airpods.lovable.app/",
    image: xoAirpodsImg
  }
];

const LinksPage = () => {
  const [showStores, setShowStores] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex flex-col items-center px-4 py-8">
      {/* Background glow - Enhanced */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md flex flex-col items-center">
        {/* Logo */}
        <motion.img
          src={villatechLogo}
          alt="VillaTech"
          className="h-16 w-auto mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        />

        {/* Category Icons - Glassmorphism */}
        <motion.div
          className="flex items-center gap-3 mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          {categories.map((category, index) => (
            <motion.div
              key={category.label}
              className={`w-12 h-12 rounded-full backdrop-blur-xl bg-zinc-900/60 border border-white/20 flex items-center justify-center ${category.color} transition-all duration-300
                shadow-[inset_2px_2px_4px_rgba(0,0,0,0.4),inset_-2px_-2px_4px_rgba(59,130,246,0.1),0_0_20px_rgba(59,130,246,0.15)]
                hover:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.5),inset_-2px_-2px_4px_rgba(59,130,246,0.15),0_0_30px_rgba(59,130,246,0.25)]
                hover:border-blue-500/40`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                scale: 1,
                y: [0, -6, 0]
              }}
              transition={{ 
                opacity: { delay: 0.4 + index * 0.1, duration: 0.3 },
                scale: { delay: 0.4 + index * 0.1, duration: 0.3 },
                y: { 
                  duration: 2.5 + index * 0.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: index * 0.2
                }
              }}
              whileHover={{ scale: 1.1 }}
              aria-label={category.label}
            >
              <category.icon className="w-5 h-5" />
            </motion.div>
          ))}
        </motion.div>

        {/* Quick Action Buttons - Neumorphic */}
        {/* Featured Card - Membresías - Neo-card prominent */}
        <motion.a
          href="https://villatech.lovable.app"
          target="_blank"
          rel="noopener noreferrer"
          className="relative w-full h-48 rounded-3xl overflow-hidden group mb-4 border border-blue-400/20
            bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900
            shadow-[12px_12px_24px_rgba(0,0,0,0.5),-12px_-12px_24px_rgba(59,130,246,0.08),0_0_50px_rgba(59,130,246,0.4)]
            hover:shadow-[15px_15px_30px_rgba(0,0,0,0.6),-15px_-15px_30px_rgba(59,130,246,0.1),0_0_70px_rgba(59,130,246,0.5)]
            transition-shadow duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          whileHover={{ scale: 1.02, y: -4 }}
        >
          {/* Glass overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.15),transparent_50%)]" />
          
          {/* Content */}
          <div className="absolute inset-0 p-5 flex flex-col justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl backdrop-blur-md bg-white/10 border border-white/20 flex items-center justify-center
                shadow-[inset_3px_3px_6px_rgba(0,0,0,0.3),inset_-3px_-3px_6px_rgba(255,255,255,0.1)]">
                <img src={villatechWingIcon} alt="VillaTech" className="w-9 h-9 object-contain" />
              </div>
              <h3 className="text-lg font-bold text-white leading-tight">Membresías VillaTech</h3>
            </div>
            
            <div>
              <p className="text-blue-100 text-sm mb-2">Ahorra hasta 30% en reparaciones y accesorios</p>
              <span className="text-white text-sm font-medium group-hover:underline">
                Activar Membresía →
              </span>
            </div>
          </div>
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </motion.a>

        {/* Secondary Links Grid - Neumorphic + Glass */}
        <motion.div
          className="grid grid-cols-2 gap-3 w-full mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <motion.a
            href="https://crm.villatechcr.com/widget/form/98xW5aC84sz3BXDKcu1q"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-3 p-6 rounded-3xl transition-all duration-300 group
              bg-gradient-to-br from-zinc-900/90 to-zinc-950/90 backdrop-blur-xl border border-white/10 hover:border-amber-500/30
              shadow-[10px_10px_20px_rgba(0,0,0,0.5),-10px_-10px_20px_rgba(251,191,36,0.04),inset_2px_2px_4px_rgba(255,255,255,0.05),0_0_30px_rgba(251,191,36,0.15)]
              hover:shadow-[8px_8px_16px_rgba(0,0,0,0.6),-8px_-8px_16px_rgba(251,191,36,0.06),inset_2px_2px_4px_rgba(255,255,255,0.08),0_0_45px_rgba(251,191,36,0.25)]"
            whileHover={{ scale: 1.02, y: -4 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center text-amber-400 group-hover:bg-amber-500/30 transition-colors
              shadow-[inset_2px_2px_4px_rgba(0,0,0,0.3),inset_-2px_-2px_4px_rgba(251,191,36,0.1)]">
              <Wrench className="w-7 h-7" />
            </div>
            <div className="text-center">
              <p className="text-white font-medium">Taller</p>
              <p className="text-zinc-400 text-xs">Agenda tu cita</p>
            </div>
          </motion.a>

          <motion.a
            href="https://www.villatechcr.com"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-3 p-6 rounded-3xl transition-all duration-300 group
              bg-gradient-to-br from-zinc-900/90 to-zinc-950/90 backdrop-blur-xl border border-white/10 hover:border-blue-500/30
              shadow-[10px_10px_20px_rgba(0,0,0,0.5),-10px_-10px_20px_rgba(59,130,246,0.04),inset_2px_2px_4px_rgba(255,255,255,0.05),0_0_30px_rgba(59,130,246,0.2)]
              hover:shadow-[8px_8px_16px_rgba(0,0,0,0.6),-8px_-8px_16px_rgba(59,130,246,0.06),inset_2px_2px_4px_rgba(255,255,255,0.08),0_0_45px_rgba(59,130,246,0.3)]"
            whileHover={{ scale: 1.02, y: -4 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center text-blue-400 group-hover:bg-blue-500/30 transition-colors
              shadow-[inset_2px_2px_4px_rgba(0,0,0,0.3),inset_-2px_-2px_4px_rgba(59,130,246,0.1)]">
              <Globe className="w-7 h-7" />
            </div>
            <div className="text-center">
              <p className="text-white font-medium">Web Oficial</p>
              <p className="text-zinc-400 text-xs">Compra en línea</p>
            </div>
          </motion.a>
        </motion.div>

        {/* Quick Action Buttons - Escríbenos & Tiendas */}
        <motion.div
          className="flex gap-3 w-full mb-6"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.4 }}
        >
          <motion.button
            onClick={() => {
              const newState = !showStores;
              setShowStores(newState);
              if (newState) {
                setTimeout(() => {
                  document.getElementById('tiendas-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 150);
              }
            }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl font-medium text-white transition-all duration-300
              bg-gradient-to-br from-zinc-800 to-zinc-900 border border-white/10
              shadow-[6px_6px_12px_rgba(0,0,0,0.5),-6px_-6px_12px_rgba(59,130,246,0.05),0_0_20px_rgba(59,130,246,0.2)]
              hover:shadow-[4px_4px_8px_rgba(0,0,0,0.6),-4px_-4px_8px_rgba(59,130,246,0.08),0_0_35px_rgba(59,130,246,0.3)]
              hover:border-blue-500/30"
            whileHover={{ scale: 1.02, y: -2 }}
          >
            <MapPin className="w-4 h-4" />
            Tiendas
            <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${showStores ? 'rotate-180' : ''}`} />
          </motion.button>
          <motion.a
            href="https://wa.me/50663217695"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-3.5 px-4 rounded-2xl font-medium text-white transition-all duration-300
              bg-gradient-to-br from-green-600 to-green-700 border border-green-400/40
              shadow-[6px_6px_12px_rgba(0,0,0,0.4),-6px_-6px_12px_rgba(34,197,94,0.15),0_0_30px_rgba(34,197,94,0.4),0_0_60px_rgba(34,197,94,0.2)]
              hover:shadow-[4px_4px_8px_rgba(0,0,0,0.5),-4px_-4px_8px_rgba(34,197,94,0.2),0_0_50px_rgba(34,197,94,0.5),0_0_80px_rgba(34,197,94,0.3)]"
            whileHover={{ scale: 1.02, y: -2 }}
          >
            <WhatsAppIcon className="w-5 h-5" />
            Escríbenos
          </motion.a>
        </motion.div>

        {/* Productos en Oferta Section - Moves below stores when visible */}
        <AnimatePresence mode="wait">
          {!showStores && (
            <motion.div
              className="w-full mb-6 mt-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
                <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">
                  Productos en Oferta
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {offerProducts.map((product, index) => (
                  <motion.a
                    key={product.name}
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative overflow-hidden rounded-3xl transition-all duration-300 group
                      bg-gradient-to-br from-zinc-900/90 to-zinc-950/90 backdrop-blur-xl border border-white/10 hover:border-blue-500/30
                      shadow-[10px_10px_20px_rgba(0,0,0,0.5),-10px_-10px_20px_rgba(59,130,246,0.04),inset_2px_2px_4px_rgba(255,255,255,0.05),0_0_30px_rgba(59,130,246,0.15)]
                      hover:shadow-[8px_8px_16px_rgba(0,0,0,0.6),-8px_-8px_16px_rgba(59,130,246,0.06),inset_2px_2px_4px_rgba(255,255,255,0.08),0_0_45px_rgba(59,130,246,0.25)]"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                  >
                    {/* Badge OFERTA */}
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-blue-500 to-blue-600 
                      px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-lg z-10">
                      OFERTA
                    </div>
                    
                    {/* Product Image */}
                    <div className="h-28 bg-zinc-800 flex items-center justify-center relative overflow-hidden">
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="p-3">
                      <h4 className="text-white font-semibold text-sm mb-1">{product.name}</h4>
                      <p className="text-zinc-400 text-[10px] leading-tight mb-2 line-clamp-2">{product.description}</p>
                      <span className="text-blue-400 text-xs font-medium flex items-center gap-1 group-hover:text-blue-300 transition-colors">
                        Ver oferta <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stores Section - Premium Neumorphic */}
        <AnimatePresence>
          {showStores && (
            <motion.div
              id="tiendas-section"
              className="w-full space-y-3 pt-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {stores.map((store, index) => (
                <motion.div
                  key={store.name}
                  className="p-5 rounded-3xl transition-all duration-300
                    bg-gradient-to-br from-zinc-900/80 to-zinc-950/80 backdrop-blur-xl border border-white/10 hover:border-blue-500/30
                    shadow-[10px_10px_20px_rgba(0,0,0,0.5),-10px_-10px_20px_rgba(59,130,246,0.04),inset_2px_2px_4px_rgba(0,0,0,0.3),inset_-2px_-2px_4px_rgba(59,130,246,0.06),0_0_25px_rgba(59,130,246,0.2)]
                    hover:shadow-[8px_8px_16px_rgba(0,0,0,0.6),-8px_-8px_16px_rgba(59,130,246,0.06),inset_2px_2px_4px_rgba(0,0,0,0.4),inset_-2px_-2px_4px_rgba(59,130,246,0.1),0_0_40px_rgba(59,130,246,0.3)]"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.02, y: -4 }}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-500/20 flex items-center justify-center flex-shrink-0
                      shadow-[inset_2px_2px_4px_rgba(0,0,0,0.3),inset_-2px_-2px_4px_rgba(59,130,246,0.1)]">
                      <MapPin className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-lg">{store.name}</p>
                      <p className="text-zinc-400 text-sm">{store.location}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <a 
                      href={`tel:${store.phone.replace(/\s/g, '')}`}
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-zinc-300 hover:text-white text-sm font-medium transition-all duration-300
                        bg-zinc-800/90 backdrop-blur-sm border border-white/5
                        shadow-[inset_3px_3px_6px_rgba(0,0,0,0.4),inset_-3px_-3px_6px_rgba(255,255,255,0.05)]
                        hover:shadow-[inset_2px_2px_4px_rgba(0,0,0,0.5),inset_-2px_-2px_4px_rgba(255,255,255,0.08),0_0_15px_rgba(255,255,255,0.1)]"
                    >
                      <Phone className="w-4 h-4" />
                      Llamar
                    </a>
                    <a 
                      href={store.waze}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-medium transition-all duration-300
                        bg-gradient-to-br from-blue-600 to-blue-700 border border-blue-400/30
                        shadow-[4px_4px_8px_rgba(0,0,0,0.4),-4px_-4px_8px_rgba(59,130,246,0.1),0_0_20px_rgba(59,130,246,0.3)]
                        hover:shadow-[3px_3px_6px_rgba(0,0,0,0.5),-3px_-3px_6px_rgba(59,130,246,0.15),0_0_30px_rgba(59,130,246,0.4)]"
                    >
                      <Navigation className="w-4 h-4" />
                      Waze
                    </a>
                  </div>
                </motion.div>
              ))}

              {/* Productos en Oferta - Shown below stores */}
              <motion.div
                className="w-full mt-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
                  <span className="text-blue-400 text-sm font-semibold uppercase tracking-wider">
                    Productos en Oferta
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {offerProducts.map((product, index) => (
                    <motion.a
                      key={product.name}
                      href={product.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative overflow-hidden rounded-3xl transition-all duration-300 group
                        bg-gradient-to-br from-zinc-900/90 to-zinc-950/90 backdrop-blur-xl border border-white/10 hover:border-blue-500/30
                        shadow-[10px_10px_20px_rgba(0,0,0,0.5),-10px_-10px_20px_rgba(59,130,246,0.04),inset_2px_2px_4px_rgba(255,255,255,0.05),0_0_30px_rgba(59,130,246,0.15)]
                        hover:shadow-[8px_8px_16px_rgba(0,0,0,0.6),-8px_-8px_16px_rgba(59,130,246,0.06),inset_2px_2px_4px_rgba(255,255,255,0.08),0_0_45px_rgba(59,130,246,0.25)]"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 + index * 0.1 }}
                      whileHover={{ scale: 1.02, y: -4 }}
                    >
                      {/* Badge OFERTA */}
                      <div className="absolute top-2 right-2 bg-gradient-to-r from-blue-500 to-blue-600 
                        px-2 py-0.5 rounded-full text-[10px] font-bold text-white shadow-lg z-10">
                        OFERTA
                      </div>
                      
                      {/* Product Image */}
                      <div className="h-28 bg-zinc-800 flex items-center justify-center relative overflow-hidden">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      
                      {/* Info */}
                      <div className="p-3">
                        <h4 className="text-white font-semibold text-sm mb-1">{product.name}</h4>
                        <p className="text-zinc-400 text-[10px] leading-tight mb-2 line-clamp-2">{product.description}</p>
                        <span className="text-blue-400 text-xs font-medium flex items-center gap-1 group-hover:text-blue-300 transition-colors">
                          Ver oferta <ExternalLink className="w-3 h-3" />
                        </span>
                      </div>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer */}
        <motion.p
          className="mt-10 text-zinc-600 text-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        >
          © 2025 VillaTech Costa Rica
        </motion.p>
      </div>
    </div>
  );
};

export default LinksPage;
